import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';
import {
	getStoredProfessionalCalendarView,
	setStoredProfessionalCalendarView,
	type ProfessionalCalendarView,
} from '@/pages/professional/home/utils/professionalCalendarViewStorage';

/** Mesmo intervalo da linha vermelha em `CalendarWeekView`. */
export const PROFESSIONAL_CALENDAR_NOW_TICK_MS = 30_000;

export type ProfessionalHistoryDateRange = {
	start: string | null;
	end: string | null;
};

const EMPTY_HISTORY_DATE_RANGE: ProfessionalHistoryDateRange = {
	start: null,
	end: null,
};

type ProfessionalCalendarContextValue = {
	now: Date;
	currentDate: Date;
	setCurrentDate: (date: Date) => void;
	view: ProfessionalCalendarView;
	handleViewChange: (next: ProfessionalCalendarView) => void;
	weekScrollAlignKey: number;
	bumpWeekScrollAlignKey: () => void;
	handleToday: () => void;
	handlePrev: () => void;
	handleNext: () => void;
	nextDisabled: boolean;
	monthName: string;
	year: number;
	isHistoryPanelOpen: boolean;
	toggleHistoryPanel: () => void;
	setHistoryPanelOpen: (open: boolean) => void;
	historyStatusFilter: Sessions.SessionStatus | null;
	setHistoryStatusFilter: (status: Sessions.SessionStatus | null) => void;
	historyDateRange: ProfessionalHistoryDateRange;
	setHistoryDateRange: (range: ProfessionalHistoryDateRange) => void;
};

const ProfessionalCalendarContext =
	createContext<ProfessionalCalendarContextValue | null>(null);

const ProfessionalCalendarProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const [now, setNow] = useState(() => new Date());
	const [currentDate, setCurrentDate] = useState(new Date());
	const [view, setView] = useState<ProfessionalCalendarView>(
		getStoredProfessionalCalendarView,
	);
	const [weekScrollAlignKey, setWeekScrollAlignKey] = useState(0);
	const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
	const [historyStatusFilter, setHistoryStatusFilter] =
		useState<Sessions.SessionStatus | null>(null);
	const [historyDateRange, setHistoryDateRange] =
		useState<ProfessionalHistoryDateRange>(EMPTY_HISTORY_DATE_RANGE);

	const resetHistoryFilters = useCallback(() => {
		setHistoryStatusFilter(null);
		setHistoryDateRange(EMPTY_HISTORY_DATE_RANGE);
	}, []);

	const setHistoryPanelOpen = useCallback(
		(open: boolean) => {
			setIsHistoryPanelOpen(open);
			if (!open) resetHistoryFilters();
		},
		[resetHistoryFilters],
	);

	const toggleHistoryPanel = useCallback(() => {
		setIsHistoryPanelOpen((open) => {
			if (open) resetHistoryFilters();
			return !open;
		});
	}, [resetHistoryFilters]);

	useEffect(() => {
		const tick = () => setNow(new Date());
		tick();
		const timer = window.setInterval(
			tick,
			PROFESSIONAL_CALENDAR_NOW_TICK_MS,
		);
		return () => window.clearInterval(timer);
	}, []);

	const today = new Date();
	const maxLimitDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);

	const handleViewChange = useCallback((next: ProfessionalCalendarView) => {
		setView(next);
		setStoredProfessionalCalendarView(next);
	}, []);

	const bumpWeekScrollAlignKey = useCallback(() => {
		setWeekScrollAlignKey((key) => key + 1);
	}, []);

	const nextDisabled = useMemo(() => {
		if (view === 'MONTH') {
			const nextMonth = new Date(
				currentDate.getFullYear(),
				currentDate.getMonth() + 1,
				1,
			);
			return nextMonth > maxLimitDate;
		}
		const nextWeek = new Date(
			currentDate.getTime() + 7 * 24 * 60 * 60 * 1000,
		);
		return nextWeek > maxLimitDate;
	}, [currentDate, maxLimitDate, view]);

	const handlePrev = useCallback(() => {
		if (view === 'MONTH') {
			setCurrentDate(
				new Date(
					currentDate.getFullYear(),
					currentDate.getMonth() - 1,
					1,
				),
			);
		} else {
			setCurrentDate(
				new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000),
			);
		}
	}, [currentDate, view]);

	const handleNext = useCallback(() => {
		if (nextDisabled) return;
		if (view === 'MONTH') {
			setCurrentDate(
				new Date(
					currentDate.getFullYear(),
					currentDate.getMonth() + 1,
					1,
				),
			);
		} else {
			setCurrentDate(
				new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000),
			);
		}
	}, [currentDate, nextDisabled, view]);

	const handleToday = useCallback(() => {
		setCurrentDate(new Date());
		if (view === 'MONTH') {
			setView('WEEK');
			setStoredProfessionalCalendarView('WEEK');
		}
		bumpWeekScrollAlignKey();
	}, [bumpWeekScrollAlignKey, view]);

	const monthName = currentDate.toLocaleString('pt-BR', { month: 'long' });
	const year = currentDate.getFullYear();

	const value = useMemo(
		() => ({
			now,
			currentDate,
			setCurrentDate,
			view,
			handleViewChange,
			weekScrollAlignKey,
			bumpWeekScrollAlignKey,
			handleToday,
			handlePrev,
			handleNext,
			nextDisabled,
			monthName,
			year,
			isHistoryPanelOpen,
			toggleHistoryPanel,
			setHistoryPanelOpen,
			historyStatusFilter,
			setHistoryStatusFilter,
			historyDateRange,
			setHistoryDateRange,
		}),
		[
			now,
			currentDate,
			view,
			handleViewChange,
			weekScrollAlignKey,
			bumpWeekScrollAlignKey,
			handleToday,
			handlePrev,
			handleNext,
			nextDisabled,
			monthName,
			year,
			isHistoryPanelOpen,
			toggleHistoryPanel,
			setHistoryPanelOpen,
			historyStatusFilter,
			historyDateRange,
		],
	);

	return (
		<ProfessionalCalendarContext.Provider value={value}>
			{children}
		</ProfessionalCalendarContext.Provider>
	);
};

const useProfessionalCalendar = () => {
	const context = useContext(ProfessionalCalendarContext);
	if (!context) {
		throw new Error(
			'useProfessionalCalendar must be used within ProfessionalCalendarProvider',
		);
	}
	return context;
};

export { ProfessionalCalendarProvider, useProfessionalCalendar };
